// PECR / UK GDPR cookie consent helpers.
// Stored in a first-party cookie so essential infra (Supabase auth) is unaffected.

export type CookieConsent = {
  essential: true;
  analytics: boolean;
  preferences: boolean;
  decidedAt: string; // ISO date
};

const COOKIE_NAME = "nursly_cookie_consent";
const MAX_AGE_DAYS = 365;

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(name + "="));
  return match ? decodeURIComponent(match.split("=")[1]) : null;
}

function writeCookie(name: string, value: string) {
  if (typeof document === "undefined") return;
  const expires = new Date(Date.now() + MAX_AGE_DAYS * 864e5).toUTCString();
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Expires=${expires}; SameSite=Lax${secure}`;
}

export function getCookieConsent(): CookieConsent | null {
  const raw = readCookie(COOKIE_NAME);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return {
      essential: true,
      analytics: !!parsed.analytics,
      preferences: !!parsed.preferences,
      decidedAt: parsed.decidedAt || new Date().toISOString(),
    };
  } catch {
    return null;
  }
}

export function saveCookieConsent(consent: Omit<CookieConsent, "essential" | "decidedAt">) {
  const full: CookieConsent = {
    essential: true,
    analytics: !!consent.analytics,
    preferences: !!consent.preferences,
    decidedAt: new Date().toISOString(),
  };
  writeCookie(COOKIE_NAME, JSON.stringify(full));
  window.dispatchEvent(new CustomEvent("nursly:consent-changed", { detail: full }));
  return full;
}

export function hasDecidedConsent(): boolean {
  return getCookieConsent() !== null;
}
